import { useMsal } from '@azure/msal-react'
import { ShieldAlert, RefreshCw } from 'lucide-react'
import { useBrandingStore } from '../stores/branding'
import { useAuthStore } from '../stores/auth'

interface AccessBlockedPageProps {
  // Passed down from App.tsx's single useCurrentUser() call site rather
  // than this component calling the hook itself -- a second observer on
  // the same query key refetches on every mount (staleTime defaults to 0),
  // which re-runs a second copy of the store-syncing effect and produces a
  // genuine infinite refetch loop. See AuthenticatedApp's comment.
  onRetry: () => void
}

// Catch-all page for authStatus === 'blocked' -- every authMe rejection that
// isn't one of the five codes with their own dedicated page (pending,
// no_invite, denied, external, not_provisioned). Covers both named codes
// with their own message (lib/authErrorMessages.ts's KNOWN_MESSAGES) and
// any code that isn't recognized at all. Retry is only offered when
// blockedInfo.retryable is true (REGISTRY_UNAVAILABLE, DB_UNAVAILABLE, or a
// raw network failure) -- every other reason here is permanent until
// someone acts, and offering Retry for those is the exact bug this page
// exists to fix.
//
// `link`, when present, points the reader at Raven Automation instead of
// "your administrator" -- lib/authErrorMessages.ts is the single place
// that decides, per code, which party can actually resolve it.
export default function AccessBlockedPage({ onRetry }: AccessBlockedPageProps) {
  const { instance } = useMsal()
  const { name } = useBrandingStore()
  const { blockedInfo } = useAuthStore()

  const message = blockedInfo?.message
    ?? "We couldn't verify your access, and we don't recognize why. Contact Raven Automation if this continues."
  const retryable = blockedInfo?.retryable ?? false
  const link = blockedInfo?.link

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <ShieldAlert className="h-8 w-8 text-destructive" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">
            {retryable ? `${name} is temporarily unavailable` : 'Access not available'}
          </h1>
          <p className="text-sm text-muted-foreground">{message}</p>
          {link && (
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-sm text-primary underline underline-offset-4 hover:opacity-80"
            >
              {link.label}
            </a>
          )}
        </div>
        {retryable && (
          <button
            onClick={onRetry}
            className="mx-auto flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Try again
          </button>
        )}
        <button
          onClick={() => instance.logoutRedirect()}
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
